import { useNavigate } from 'react-router-dom';
import { onboardingSteps } from '../../hooks/useOnboarding';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export function OnboardingWizard({ onboarding }) {
  const nav = useNavigate();
  const { state, currentStep, next, prev, skip, finish } = onboarding;

  if (!state.open || state.completed || state.skipped) return null;

  const idx = state.stepIndex;
  const isLast = idx === onboardingSteps.length - 1;

  const handlePrimary = () => {
    const action = currentStep.action;

    if (isLast) {
      finish();
      return;
    }

    if (action?.type === 'route' && action.to) {
      // Navigate to the action route; wizard stays resumable
      nav(action.to);
      return;
    }

    next();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-dark-800 shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-500 to-brand-600 px-6 py-4 md:px-8 md:py-6 flex items-center justify-between">
          <div>
            <div className="text-sm text-brand-100 font-medium">
              Step {idx + 1} of {onboardingSteps.length}
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mt-1">{currentStep.title}</h2>
          </div>

          <button
            onClick={skip}
            className="rounded-full p-2 hover:bg-brand-500/80 transition-colors text-white"
            aria-label="Close wizard"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 md:px-8 md:py-8">
          <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
            {currentStep.description}
          </p>

          {currentStep.subtitle && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-dark-700 rounded-lg border border-gray-200 dark:border-dark-600">
              <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
                {currentStep.subtitle.split('\n').map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-brand-500 rounded-full" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Step progress dots */}
          <div className="mt-6 flex gap-2">
            {onboardingSteps.map((s, i) => (
              <div
                key={s.id}
                className={`h-2 flex-1 rounded-full transition-colors ${
                  i <= idx ? 'bg-brand-500' : 'bg-gray-200 dark:bg-dark-600'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 dark:bg-dark-700 px-6 py-4 md:px-8 md:py-6 flex items-center justify-between gap-3 border-t border-gray-200 dark:border-dark-600">
          <button
            onClick={prev}
            disabled={idx === 0}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
          >
            <ChevronLeft size={18} />
            Back
          </button>

          <button
            onClick={skip}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
          >
            Skip for now
          </button>

          <button
            onClick={handlePrimary}
            className="flex items-center gap-2 px-5 py-2.5 bg-brand-500 hover:bg-brand-600 text-white text-sm font-semibold rounded-lg transition-colors shadow-md hover:shadow-lg"
          >
            {isLast ? 'Finish' : currentStep.cta ?? 'Continue'}
            {!isLast && <ChevronRight size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}
